<template>
  <div>
    <RecommendationAlgoliaBundle
      v-if="
        isAlgoliaRecommendationBoughtTogetherActive &&
        selectedOffer &&
        selectedOffer.offerLegacyId &&
        selectedOffer.price
      "
      class="mb-72 overflow-hidden"
      :main-product="{
        title: product.titles.default,
        description: product.subtitleElements.join(' - '),
        image: product.images?.[0]?.url,
        productPageLink: {
          type: 'internal',
          name: PRODUCT.HOME,
          params: routeParams,
          query: {},
          hash: {},
        },
        grade: selectedOffer.grade,
        price: selectedOffer.price,
        priceNew: product.priceWhenNew,
        productId: product.productId,
        listingId: `${selectedOffer?.offerLegacyId}`,
        brand: product.brand,
        category: product.tracking.categoryName,
        id: product.productId,
        model: product.model ?? '',
        specifications: {},
        listingPublicId: selectedOffer?.offerId,
        mobilePlanOfferId: mobilePlan,
        partnerPromoCodes: product.includedServiceOffers.partnerPromoCodes,
      }"
      :title="i18n(translations.bundleTitle)"
      @add-to-cart="onBundleAddToCart"
    />
    <RecommendationBundle
      v-else-if="
        !isAlgoliaRecommendationBoughtTogetherActive &&
        selectedOffer &&
        selectedOffer.offerLegacyId &&
        selectedOffer.price
      "
      class="mb-72 overflow-hidden"
      :main-product="{
        title: product.titles.default,
        description: product.subtitleElements.join(' - '),
        image: product.images?.[0]?.url,
        productPageLink: {
          type: 'internal',
          name: PRODUCT.HOME,
          params: routeParams,
          query: {},
          hash: {},
        },
        grade: selectedOffer.grade,
        price: selectedOffer.price,
        priceNew: product.priceWhenNew,
        productId: product.productId,
        listingId: `${selectedOffer?.offerLegacyId}`,
        brand: product.brand,
        category: product.tracking.categoryName,
        id: product.productId,
        model: product.model ?? '',
        specifications: {},
        listingPublicId: selectedOffer?.offerId,
        mobilePlanOfferId: mobilePlan,
        partnerPromoCodes: product.includedServiceOffers.partnerPromoCodes,
      }"
      :title="i18n(translations.bundleTitle)"
      @add-to-cart="onBundleAddToCart"
    />

    <RecommendationAlgoliaCarousel
      v-if="isAlgoliaRecommendationActive"
      class="mb-72"
      :recommendation-query="{
        objectID: algoliaObjectId,
        model: 'related-products',
        options: {
          maxRecommendations: 4,
          ...(product.tracking.categoryId && {
            fallbackParameters: {
              filters: `('cat_id':'${product.tracking.categoryId}')`,
            },
          }),
        },
      }"
      :title="
        withRanking
          ? i18n(translations.productRanking)
          : i18n(translations.moreTitle)
      "
      :trackingData="{
        list: 'product page cousins 1',
      }"
      :with-add-to-cart="false"
      @error="onRecommendationsError"
      @loaded="onRecommendationsLoaded"
    />

    <RecommendationCarousel
      v-else
      class="mb-72"
      :options="{
        withCrossedPrice: true,
        withStartingFrom: true,
        withGrade: true,
        withAddToCart: false,
      }"
      :recommendation-query="{
        category: 'cousinsUp',
        scope: 'product',
        scopeId: productId,
        limit: 4,
      }"
      :title="
        withRanking
          ? i18n(translations.productRanking)
          : i18n(translations.moreTitle)
      "
      :trackingData="{
        list: 'product page cousins 1',
      }"
      @error="onRecommendationsError"
      @loaded="onRecommendationsLoaded"
    >
      <template v-if="withRanking" #post-card="{ index }">
        <RankingTag :index />
      </template>
    </RecommendationCarousel>

    <RecommendationAlgoliaCarousel
      v-if="isAlgoliaRecommendationBoughtTogetherActive"
      class="mb-72"
      :recommendation-query="{
        objectID: algoliaObjectId,
        model: 'bought-together',
        options: {
          maxRecommendations: 4,
          queryParameters: {
            ruleContexts: ['only_accessory', 'only_compatible'],
          },
        },
      }"
      :title="i18n(translations.crossTitle)"
      :trackingData="{
        list: 'product page cousins 2',
      }"
      :with-add-to-cart="false"
    />
    <RecommendationCarousel
      v-else
      class="mb-72"
      :options="{
        withCrossedPrice: true,
        withStartingFrom: true,
        withGrade: true,
        withAddToCart: false,
      }"
      :recommendation-query="{
        category: 'cousinsDown',
        scope: 'product',
        scopeId: productId,
        limit: 4,
      }"
      :title="i18n(translations.crossTitle)"
      :trackingData="{
        list: 'product page cousins 2',
      }"
    />
  </div>
</template>

<script lang="ts" setup>
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import RecommendationAlgoliaCarousel from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import RecommendationCarousel from '@backmarket/nuxt-layer-recommendation/RecommendationCarousel.vue'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { PRODUCT } from '~/scopes/product-core/route-names'
import RecommendationAlgoliaBundle from '~/scopes/recommendation/components/RecommendationAlgoliaBundle.vue'
import RecommendationBundle from '~/scopes/recommendation/components/RecommendationBundle.vue'

import { useProductUrlToAlgoliaObjectId } from '../../composables/useProductUrlToAlgoliaObjectId'
import { useUrlParams } from '../../composables/useUrlParams'

import translations from './Recommendations.translations'
import RankingTag from './components/RankingTag/RankingTag.vue'

const { selectedOffer, product, withRanking } = defineProps<{
  product: GetProductResponse
  selectedOffer: GetPickersResponse['selectedOffer']
  withRanking?: boolean
}>()

const emit = defineEmits<{
  'bundle-add-to-cart': [Array<string>, Product | null]
  'recommendations-loaded': [Array<Product>]
  'recommendations-error': []
}>()

const i18n = useI18n()
const { productId, mobilePlan, slug } = useUrlParams()
const routeParams = { uuid: productId, slugV2: slug }
const experiments = useExperiments()
const isAlgoliaRecommendationActive =
  experiments['experiment.algoliaRecommend'] === 'Algolia'
const isAlgoliaRecommendationBoughtTogetherActive =
  experiments['experiment.algoliaRecommendBoughtTogether'] === 'Algolia'
const algoliaObjectId = useProductUrlToAlgoliaObjectId()

function onBundleAddToCart(
  listings: Array<string>,
  bundledProduct: Product | null,
) {
  emit('bundle-add-to-cart', listings, bundledProduct)
}

function onRecommendationsLoaded(products: Array<Product>) {
  emit('recommendations-loaded', products)
}

function onRecommendationsError() {
  emit('recommendations-error')
}
</script>
